import { useState, useEffect, useMemo, FormEvent } from 'react';
import { getTeams, getPlayers, getPlayersByTeam, createTeam, updateTeam, deleteTeam, getVenues, COACH_TITLES, DAYS_OF_WEEK, DAY_TIMES, TEAM_CATEGORIES, AGE_GROUPS, Team, Coach, Player, Venue, TeamSponsor } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import BodyField from '../components/BodyField';
import { hasText, previewText, richTextAllowed } from '../utils/richText';
import { Link } from 'react-router-dom';
import { normalizeUrlInput } from '../utils/url';
import ImageUploader from '../components/ImageUploader';
import TeamNav from '../components/TeamNav';
import TeamWizard, { WizardStep } from '../components/teamwizard/TeamWizard';
import './Teams.css';
import { overlayClose } from '../utils/overlayClose';

function generateCoachId(): string {
  return `coach-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
}

/** Columns the table view can sort by. */
type SortKey = 'name' | 'ageGroup' | 'teamCategory' | 'coaches' | 'squad';
type SortState = { key: SortKey; dir: 'asc' | 'desc' };

/** "boys" -> "Boys", using the same wording as the Team For picker. */
function categoryLabel(value?: string): string {
  const v = (value || '').trim();
  if (!v) return '';
  const match = TEAM_CATEGORIES.find(c => c.value === v);
  return match ? match.label : v.charAt(0).toUpperCase() + v.slice(1);
}

/** Sort age groups by the ladder, not the alphabet, "Under 9" precedes
 *  "Under 10". Anything off the list sorts last. */
function ageGroupRank(value?: string): number {
  const i = AGE_GROUPS.indexOf((value || '').trim());
  return i === -1 ? AGE_GROUPS.length : i;
}

/** Team For sorts in the same order as the public page's sections; unset last. */
function categoryRank(value?: string): number {
  const v = (value || '').trim();
  if (!v) return TEAM_CATEGORIES.length;
  const i = TEAM_CATEGORIES.findIndex(c => c.value === v);
  return i === -1 ? TEAM_CATEGORIES.length : i;
}

/** Counts the legacy single `coach` string as one, same as the cell renders. */
function coachCount(team: Team): number {
  if (team.coaches && team.coaches.length > 0) return team.coaches.length;
  return team.coach ? 1 : 0;
}

const byText = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });

/**
 * Ordering for one table column. Coaches sorts by COUNT, not name: the useful
 * question in a twenty-team table is "which teams still have nobody?", and
 * ascending puts those at the top next to their red "None yet". Every column
 * falls back to the team name so equal rows keep a predictable order.
 */
function compareTeams(a: Team, b: Team, key: SortKey, squadOf: (t: Team) => number): number {
  switch (key) {
    case 'ageGroup': return ageGroupRank(a.ageGroup) - ageGroupRank(b.ageGroup) || byText(a.name, b.name);
    case 'teamCategory': return categoryRank(a.teamCategory) - categoryRank(b.teamCategory) || byText(a.name, b.name);
    case 'coaches': return coachCount(a) - coachCount(b) || byText(a.name, b.name);
    case 'squad': return squadOf(a) - squadOf(b) || byText(a.name, b.name);
    default: return byText(a.name, b.name);
  }
}

function Teams() {
  const { plan, features } = useAuth();

  // Cards or table. Twenty-team clubs (Whiteknights) live in the table; small
  // clubs prefer the cards. Remembered so the choice survives a reload.
  const [view, setView] = useState<'cards' | 'table'>(() => {
    try {
      return localStorage.getItem('gfc.teamsView') === 'table' ? 'table' : 'cards';
    } catch {
      return 'cards';
    }
  });
  useEffect(() => {
    try { localStorage.setItem('gfc.teamsView', view); } catch { /* private mode */ }
  }, [view]);

  // Table sorting. null = the order the API returned, which is what the table
  // showed before this existed and what the cards view still shows.
  const [sort, setSort] = useState<SortState | null>(null);
  const isPaid = plan === 'club' || plan === 'pro';
  const [teams, setTeams] = useState<Team[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [playerCounts, setPlayerCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [coachUploading, setCoachUploading] = useState<Record<string, boolean>>({});
  const isUploading = uploadingCount > 0;

  // Wizard ("Add your team & squad")
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardTeam, setWizardTeam] = useState<Team | null>(null);
  const [wizardStart, setWizardStart] = useState<WizardStep>('basics');

  // "View team", a card view of the team's squad
  const [viewTeam, setViewTeam] = useState<Team | null>(null);
  const [viewPlayers, setViewPlayers] = useState<Player[]>([]);
  const [viewLoading, setViewLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    ageGroup: '',
    teamCategory: '',
    hideSquad: false,
    venueId: '',
    league: '',
    matchDay: '',
    matchTime: '',
    trainingDay: '',
    trainingTime: '',
    trainingStart: '',
    trainingEnd: '',
    sponsors: [] as TeamSponsor[],
    coach: '',
    coaches: [] as Coach[],
    description: '',
    image: '',
  });

  useEffect(() => {
    loadTeams();
    getVenues().then(r => setVenues(r.venues)).catch(() => setVenues([]));
  }, []);

  // silent=true skips the full-page loading spinner. Critical for the
  // wizard's onChanged refresh: the spinner early-returns the page and
  // would UNMOUNT the open wizard mid-flow (re-mounting it fresh at
  // step 1). Initial load uses the spinner; in-wizard saves refresh
  // data silently so the wizard stays mounted + advances normally.
  const loadTeams = async (silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      const data = await getTeams();
      setTeams(data);
      // Player counts per team drive the "no squad yet" completeness hint.
      // Non-fatal if the players endpoint isn't available for this club.
      try {
        const players = await getPlayers();
        const counts: Record<string, number> = {};
        for (const p of players) {
          if (p.status === 'archived') continue;
          counts[p.teamId] = (counts[p.teamId] || 0) + 1;
        }
        setPlayerCounts(counts);
      } catch {
        setPlayerCounts({});
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load teams');
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  // Wizard launchers
  const openWizardCreate = () => {
    setWizardTeam(null);
    setWizardStart('basics');
    setWizardOpen(true);
  };
  const openWizardResume = (team: Team) => {
    const coachCount = (team.coaches || []).filter(c => c.name?.trim()).length;
    const firstIncomplete: WizardStep = coachCount === 0 ? 'coaches' : 'squad';
    setWizardTeam(team);
    setWizardStart(firstIncomplete);
    setWizardOpen(true);
  };
  const closeWizard = () => { setWizardOpen(false); setWizardTeam(null); };

  // View-team launcher: loads the team's (non-archived) squad as cards.
  const openViewTeam = async (team: Team) => {
    setViewTeam(team);
    setViewPlayers([]);
    setViewLoading(true);
    try {
      const players = await getPlayersByTeam(team.id);
      setViewPlayers(players.filter(p => p.status !== 'archived'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load squad');
    } finally {
      setViewLoading(false);
    }
  };
  const closeViewTeam = () => { setViewTeam(null); setViewPlayers([]); };

  const handleOpenModal = (team?: Team) => {
    if (team) {
      setEditingTeam(team);
      // Migrate: if team has old single coach but no coaches array, create one
      let coaches = team.coaches || [];
      if (coaches.length === 0 && team.coach) {
        coaches = [{ id: generateCoachId(), name: team.coach, image: '' }];
      }
      setFormData({
        name: team.name,
        ageGroup: team.ageGroup,
        teamCategory: team.teamCategory || '',
        hideSquad: !!team.hideSquad,
        venueId: team.venueId || '',
        league: team.league || '',
        matchDay: team.matchDay || '',
        matchTime: team.matchTime || '',
        trainingDay: team.trainingDay || '',
        trainingTime: team.trainingTime || '',
        trainingStart: team.trainingStart || '',
        trainingEnd: team.trainingEnd || '',
        sponsors: team.sponsors || [],
        coach: team.coach,
        coaches,
        description: team.description,
        image: team.image,
      });
    } else {
      setEditingTeam(null);
      setFormData({
        name: '',
        ageGroup: '',
        teamCategory: '',
        hideSquad: false,
        venueId: '',
    league: '',
    matchDay: '',
    matchTime: '',
    trainingDay: '',
    trainingTime: '',
    trainingStart: '',
    trainingEnd: '',
        sponsors: [],
        coach: '',
        coaches: [],
        description: '',
        image: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTeam(null);
    setError('');
    setUploadingCount(0);
    setCoachUploading({});
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    if (isUploading) { setError('A photo is still uploading, give it a second, then save.'); return; }
    // The plain box enforces this itself; the formatting bar cannot.
    if (!hasText(formData.description)) { setError('Add a description for the team.'); return; }
    setIsSaving(true);

    try {
      // Set legacy coach field to first coach name for backward compat
      const coachList = formData.coaches || [];
      const primaryCoach = coachList.length > 0 ? coachList[0]!.name : '';
      const payload = {
        name: formData.name,
        ageGroup: formData.ageGroup,
        teamCategory: formData.teamCategory,
        hideSquad: formData.hideSquad,
        venueId: formData.venueId,
        league: formData.league,
        matchDay: formData.matchDay,
        matchTime: formData.matchTime,
        trainingDay: formData.trainingDay,
        trainingTime: formData.trainingTime,
        trainingStart: formData.trainingStart,
        trainingEnd: formData.trainingEnd,
        sponsors: formData.sponsors,
        coach: primaryCoach,
        coaches: formData.coaches,
        description: formData.description,
        image: formData.image,
      };

      if (editingTeam) {
        await updateTeam(editingTeam.id, payload);
        setSuccessMessage('Team updated successfully');
      } else {
        await createTeam(payload);
        setSuccessMessage('Team created successfully');
      }

      await loadTeams();
      handleCloseModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save team');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this team?')) return;

    try {
      await deleteTeam(id);
      setSuccessMessage('Team deleted successfully');
      await loadTeams();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete team');
    }
  };

  const handleImageUpload = (url: string) => {
    setFormData(prev => ({ ...prev, image: url }));
  };

  // Coach management
  const addCoach = () => {
    setFormData(prev => ({
      ...prev,
      coaches: [...prev.coaches, { id: generateCoachId(), name: '', image: '', coachTitle: 'Coach' }],
    }));
  };

  const removeCoach = (coachId: string) => {
    setFormData(prev => ({
      ...prev,
      coaches: prev.coaches.filter(c => c.id !== coachId),
    }));
  };

  // Team sponsors (Club/Pro)
  const genSponsorId = () => `tsp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const addSponsor = () => setFormData(prev => ({ ...prev, sponsors: [...prev.sponsors, { id: genSponsorId(), name: '', image: '', url: '' }] }));
  const removeSponsor = (id: string) => setFormData(prev => ({ ...prev, sponsors: prev.sponsors.filter(s => s.id !== id) }));
  const updateSponsor = (id: string, field: keyof TeamSponsor, value: string) =>
    setFormData(prev => ({ ...prev, sponsors: prev.sponsors.map(s => (s.id === id ? { ...s, [field]: value } : s)) }));

  const updateCoachField = (coachId: string, field: keyof Coach, value: string) => {
    setFormData(prev => ({
      ...prev,
      coaches: prev.coaches.map(c =>
        c.id === coachId ? { ...c, [field]: value } : c
      ),
    }));
  };

  // Get display coaches for a team (prefer coaches array, fall back to legacy coach field)
  const getDisplayCoaches = (team: Team): Coach[] => {
    if (team.coaches && team.coaches.length > 0) return team.coaches;
    if (team.coach) return [{ id: 'legacy', name: team.coach, image: '', coachTitle: 'Coach' }];
    return [];
  };

  // Table rows. Sorting is entirely client-side, every team is already loaded.
  const sortedTeams = useMemo(() => {
    if (!sort) return teams;
    const squadOf = (t: Team) => playerCounts[t.id] || 0;
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...teams].sort((a, b) => sign * compareTeams(a, b, sort.key, squadOf));
  }, [teams, sort, playerCounts]);

  const toggleSort = (key: SortKey) =>
    setSort(prev => (prev && prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' }));

  const sortHeader = (key: SortKey, label: string) => {
    const active = sort?.key === key;
    return (
      <th scope="col" aria-sort={active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
        <button
          type="button"
          onClick={() => toggleSort(key)}
          className={`teams-table-sort${active ? ' is-active' : ''}`}
        >
          {label}
          <span className="teams-table-sort-arrow" aria-hidden="true">
            {active ? (sort!.dir === 'asc' ? '▲' : '▼') : '↕'}
          </span>
        </button>
      </th>
    );
  };

  if (isLoading) {
    return <div className="loading">Loading teams...</div>;
  }

  return (
    <div className="tm-page-shell teams-page">
      <TeamNav />

      <div className="page-head">
        <div>
          <h1>Teams</h1>
          <p className="subtitle">Your club's teams, squads, age groups, and coaches.</p>
        </div>
        {teams.length > 0 && (
          <div className="head-actions">
            <div className="view-toggle" role="group" aria-label="Team list view">
              <button
                type="button"
                onClick={() => setView('cards')}
                className={`ebtn ebtn-sm ${view === 'cards' ? 'ebtn-primary' : 'ebtn-secondary'}`}
                aria-pressed={view === 'cards'}
              >
                Cards
              </button>
              <button
                type="button"
                onClick={() => setView('table')}
                className={`ebtn ebtn-sm ${view === 'table' ? 'ebtn-primary' : 'ebtn-secondary'}`}
                aria-pressed={view === 'table'}
              >
                Table
              </button>
            </div>
            <button onClick={openWizardCreate} className="ebtn ebtn-primary">
              + Add team
            </button>
          </div>
        )}
      </div>

      {error && <div className="ealert ealert-error">{error}</div>}
      {successMessage && <div className="ealert ealert-success">{successMessage}</div>}

      {view === 'cards' && (
      <div className="teams-grid">
        {teams.map((team) => {
          const displayCoaches = getDisplayCoaches(team);
          const squadCount = playerCounts[team.id] || 0;
          const needsCoaches = displayCoaches.length === 0;
          const needsSquad = squadCount === 0;
          const incomplete = needsCoaches || needsSquad;
          return (
            <div key={team.id} className="team-card">
              <div className="team-image-container">
                {team.image ? (
                  <img src={team.image} alt={team.name} className="team-image" />
                ) : (
                  <div className="team-image-placeholder">No Image</div>
                )}
              </div>

              <div className="team-info">
                <h3 className="team-name">{team.name}</h3>
                <p className="team-age-group">{team.ageGroup}</p>

                {displayCoaches.length > 0 ? (
                  <div className="team-coaches-display">
                    <span className="coach-label">{displayCoaches.length === 1 ? 'Coach:' : 'Coaches:'}</span>
                    <div className="coaches-list-compact">
                      {displayCoaches.map((c, i) => (
                        <span key={c.id} className="coach-name-compact">
                          {c.image && <img src={c.image} alt="" className="coach-avatar-tiny" />}
                          {c.name}
                          {i < displayCoaches.length - 1 && ', '}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="team-incomplete-hint">No coaches yet</p>
                )}

                <p className="team-squad-count">
                  {squadCount > 0
                    ? `${squadCount} player${squadCount === 1 ? '' : 's'}`
                    : 'No squad yet'}
                </p>

                <p className="team-description">{previewText(team.description)}</p>

                <div className="team-actions">
                  <button
                    onClick={() => openViewTeam(team)}
                    className="ebtn ebtn-secondary ebtn-sm"
                  >
                    View team
                  </button>
                  {incomplete && (
                    <button
                      onClick={() => openWizardResume(team)}
                      className="ebtn ebtn-primary ebtn-sm"
                    >
                      Continue setup
                    </button>
                  )}
                  <button
                    onClick={() => handleOpenModal(team)}
                    className="ebtn ebtn-secondary ebtn-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(team.id)}
                    className="ebtn ebtn-danger ebtn-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}

      {view === 'table' && teams.length > 0 && (
        <div className="teams-table-wrap">
          <table className="teams-table">
            <thead>
              <tr>
                {sortHeader('name', 'Team')}
                {sortHeader('ageGroup', 'Age group')}
                {sortHeader('teamCategory', 'Team For')}
                {sortHeader('coaches', 'Coaches')}
                {sortHeader('squad', 'Squad')}
                <th scope="col" className="teams-table-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedTeams.map((team) => {
                const displayCoaches = getDisplayCoaches(team);
                const squadCount = playerCounts[team.id] || 0;
                return (
                  <tr key={team.id}>
                    <td data-label="Team">
                      <span className="teams-table-name">{team.name}</span>
                    </td>
                    <td data-label="Age group">{team.ageGroup || '-'}</td>
                    <td data-label="Team For">{categoryLabel(team.teamCategory) || '-'}</td>
                    <td data-label="Coaches">
                      {displayCoaches.length > 0
                        ? displayCoaches.map(c => c.name).join(', ')
                        : <span className="teams-table-warn">None yet</span>}
                    </td>
                    <td data-label="Squad">
                      {squadCount > 0
                        ? squadCount
                        : <span className="teams-table-warn">Empty</span>}
                    </td>
                    <td data-label="Actions" className="teams-table-actions">
                      <button onClick={() => openViewTeam(team)} className="ebtn ebtn-secondary ebtn-sm">View</button>
                      <button onClick={() => handleOpenModal(team)} className="ebtn ebtn-secondary ebtn-sm">Edit</button>
                      <button onClick={() => handleDelete(team.id)} className="ebtn ebtn-danger ebtn-sm">Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {teams.length === 0 && (
        <div className="empty-state">
          <p>No teams yet. Create your first team to get started.</p>
          <button onClick={openWizardCreate} className="ebtn ebtn-primary" style={{ marginTop: 16 }}>
            + Add team
          </button>
        </div>
      )}

      {wizardOpen && (
        <TeamWizard
          initialTeam={wizardTeam}
          startStep={wizardStart}
          onClose={closeWizard}
          onChanged={() => loadTeams(true)}
        />
      )}

      {/* ── View team: card view of the squad ─────────── */}
      {viewTeam && (
        <div className="modal-overlay" {...overlayClose(closeViewTeam)}>
          <div className="modal-content team-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{viewTeam.name}, squad</h2>
              <button onClick={closeViewTeam} className="modal-close">&times;</button>
            </div>

            {viewLoading ? (
              <p className="loading">Loading squad…</p>
            ) : viewPlayers.length === 0 ? (
              <div className="empty-state">
                <p>No players in this squad yet.</p>
                <button
                  className="ebtn ebtn-primary"
                  style={{ marginTop: 12 }}
                  onClick={() => { const t = viewTeam; closeViewTeam(); openWizardResume(t); }}
                >
                  Add players
                </button>
              </div>
            ) : (
              <div className="team-view-grid">
                {viewPlayers.map((p) => (
                  <div key={p.playerId} className="tv-card">
                    {p.photoUrl ? (
                      <img className="tv-avatar" src={p.photoUrl} alt="" />
                    ) : (
                      <span className="tv-avatar tv-avatar-empty">
                        {(p.firstName?.[0] || '?').toUpperCase()}{(p.lastName?.[0] || '').toUpperCase()}
                      </span>
                    )}
                    <div className="tv-body">
                      <div className="tv-name">
                        {p.firstName} {p.lastName}
                        {p.jerseyNumber != null && <span className="tv-num"> · #{p.jerseyNumber}</span>}
                      </div>
                      <div className="tv-meta">{p.position || '-'}</div>
                      {(p.consent?.parentName || p.consent?.parentEmail) && (
                        <div className="tv-guardian">
                          Guardian: {p.consent.parentName || p.consent.parentEmail}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="modal-footer">
              <button className="ebtn ebtn-secondary" onClick={closeViewTeam}>Close</button>
              {viewPlayers.length > 0 && (
                <button
                  className="ebtn ebtn-primary"
                  onClick={() => { const t = viewTeam; closeViewTeam(); openWizardResume(t); }}
                >
                  Add / edit squad
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" {...overlayClose(handleCloseModal)}>
          <div className="modal-content team-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingTeam ? 'Edit Team' : 'Add Team'}
              </h2>
              <button onClick={handleCloseModal} className="modal-close">
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name" className="form-label">Team Name</label>
                <input
                  id="name"
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Under 12s"
                />
              </div>

              <div className="form-group">
                <label htmlFor="ageGroup" className="form-label">Age Group</label>
                <select
                  id="ageGroup"
                  className="form-input"
                  value={formData.ageGroup}
                  onChange={(e) => setFormData({ ...formData, ageGroup: e.target.value })}
                  required
                >
                  <option value="">Select an age group…</option>
                  {AGE_GROUPS.map(a => <option key={a} value={a}>{a}</option>)}
                  {/* Keep whatever this club typed before the list existed, so
                      editing a team can't silently wipe its age group. */}
                  {formData.ageGroup && !AGE_GROUPS.includes(formData.ageGroup) && (
                    <option value={formData.ageGroup}>{formData.ageGroup} (existing)</option>
                  )}
                </select>
                <small className="form-hint">
                  Sets where the team appears on the public Teams page, youngest first,
                  then Open Age and Veterans.
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="teamCategory" className="form-label">Team For</label>
                <select
                  id="teamCategory"
                  className="form-input"
                  value={formData.teamCategory}
                  onChange={(e) => setFormData({ ...formData, teamCategory: e.target.value })}
                >
                  {TEAM_CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
                <small className="form-hint">
                  Groups your teams into sections on the public Teams page. Leave as
                  "Not specified" and the team simply sits in the list.
                </small>
              </div>

              {venues.length > 0 && (
                <div className="form-group">
                  <label htmlFor="venueId" className="form-label">Home venue</label>
                  <select
                    id="venueId"
                    className="form-input"
                    value={formData.venueId}
                    onChange={(e) => setFormData({ ...formData, venueId: e.target.value })}
                  >
                    <option value="">Not specified</option>
                    {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                  <small className="form-hint">
                    Where this team plays its home games. Shows on the team page and on each home
                    fixture. Manage venues under Where We Play.
                  </small>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="league" className="form-label">League</label>
                <input
                  type="text"
                  id="league"
                  className="form-input"
                  value={formData.league}
                  onChange={(e) => setFormData({ ...formData, league: e.target.value })}
                  placeholder="e.g. East Berks Football Alliance"
                  maxLength={80}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Match day</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    className="form-input"
                    aria-label="Match day"
                    value={formData.matchDay}
                    onChange={(e) => setFormData({ ...formData, matchDay: e.target.value })}
                  >
                    <option value="">Not set</option>
                    {DAYS_OF_WEEK.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select
                    className="form-input"
                    aria-label="Match time of day"
                    value={formData.matchTime}
                    onChange={(e) => setFormData({ ...formData, matchTime: e.target.value })}
                  >
                    <option value="">Any time</option>
                    {DAY_TIMES.map(t => <option key={t} value={t}>{t}s</option>)}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Training day</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    className="form-input"
                    aria-label="Training day"
                    value={formData.trainingDay}
                    onChange={(e) => setFormData({ ...formData, trainingDay: e.target.value })}
                  >
                    <option value="">Not set</option>
                    {DAYS_OF_WEEK.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select
                    className="form-input"
                    aria-label="Training time of day"
                    value={formData.trainingTime}
                    onChange={(e) => setFormData({ ...formData, trainingTime: e.target.value })}
                  >
                    <option value="">Any time</option>
                    {DAY_TIMES.map(t => <option key={t} value={t}>{t}s</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                  <input
                    type="time"
                    className="form-input"
                    aria-label="Training start time"
                    value={formData.trainingStart}
                    onChange={(e) => setFormData({ ...formData, trainingStart: e.target.value })}
                  />
                  <span style={{ opacity: 0.6 }}>to</span>
                  <input
                    type="time"
                    className="form-input"
                    aria-label="Training end time"
                    value={formData.trainingEnd}
                    onChange={(e) => setFormData({ ...formData, trainingEnd: e.target.value })}
                  />
                </div>
                <small className="form-hint">
                  These show on the team page in the same place on every team, once they're
                  set you can take them back out of the description. Times are optional:
                  leave them blank and it just reads "Thursday evenings". Filling them in
                  means we can offer parents a calendar reminder later.
                </small>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem' }}>
                  <input
                    type="checkbox"
                    checked={formData.hideSquad}
                    onChange={(e) => setFormData({ ...formData, hideSquad: e.target.checked })}
                    style={{ width: 'auto', margin: 0 }}
                  />
                  Don't show a squad on this team's public page
                </label>
                <small className="form-hint">
                  For youth teams where you'd rather not list children. The Squad
                  section is removed entirely, no "coming soon" placeholder. You can
                  still keep players in the admin for your own records.
                </small>
              </div>

              {/* Team sponsors, Club/Pro. On Free the block still shows so the
                  feature is discoverable, but reads as an upgrade rather than a form. */}
              <div className="form-group">
                <div className="coaches-section-header">
                  <label className="form-label">Team sponsors</label>
                  {isPaid && formData.sponsors.length < 6 && (
                    <button type="button" className="btn btn-secondary btn-small" onClick={addSponsor}>+ Add sponsor</button>
                  )}
                </div>
                {!isPaid ? (
                  <small className="form-hint">
                    Show this team's own sponsor, logo and link, on its page and card. Available on the
                    Club plan. <Link to="/upgrade">Upgrade to Club</Link>.
                  </small>
                ) : formData.sponsors.length === 0 ? (
                  <small className="form-hint">The local business on this team's shirts. Logo, name and a link.</small>
                ) : (
                  <div className="team-sponsors-list">
                    {formData.sponsors.map(sp => (
                      <div key={sp.id} className="team-sponsor-row">
                        <div className="team-sponsor-logo">
                          <ImageUploader
                            onUpload={url => updateSponsor(sp.id, 'image', url)}
                            currentImage={sp.image}
                            onUploadingChange={u => setUploadingCount(c => c + (u ? 1 : -1))}
                            label="Logo"
                          />
                        </div>
                        <div className="team-sponsor-fields">
                          <input type="text" className="form-input" placeholder="Sponsor name" value={sp.name} onChange={e => updateSponsor(sp.id, 'name', e.target.value)} />
                          <input type="text" className="form-input" placeholder="Website, e.g. www.example.co.uk" value={sp.url} onChange={e => updateSponsor(sp.id, 'url', e.target.value)} onBlur={e => updateSponsor(sp.id, 'url', normalizeUrlInput(e.target.value))} />
                        </div>
                        <button type="button" className="btn btn-danger btn-small" onClick={() => removeSponsor(sp.id)}>Remove</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Coaches Section */}
              <div className="form-group">
                <div className="coaches-section-header">
                  <label className="form-label">Coaches</label>
                  <button type="button" onClick={addCoach} className="ebtn ebtn-secondary ebtn-sm">
                    + Add Coach
                  </button>
                </div>

                {formData.coaches.length === 0 && (
                  <p className="coaches-empty-hint">No coaches added yet. Click "Add Coach" to add one.</p>
                )}

                <div className="coaches-form-list">
                  {formData.coaches.map((coach, index) => (
                    <div key={coach.id} className="coach-form-item">
                      <div className="coach-form-avatar">
                        {coach.image ? (
                          <div className="coach-avatar-preview">
                            <img src={coach.image} alt={coach.name} />
                            <button
                              type="button"
                              className="coach-avatar-remove"
                              onClick={() => updateCoachField(coach.id, 'image', '')}
                            >
                              &times;
                            </button>
                          </div>
                        ) : (
                          <div className="coach-avatar-placeholder">
                            {coachUploading[coach.id] && <span className="coach-avatar-spinner" role="status" aria-label="Uploading photo" />}
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                              <circle cx="12" cy="7" r="4" />
                            </svg>
                            <span className="coach-avatar-plus">+</span>
                            <ImageUploader
                              onUpload={(url) => updateCoachField(coach.id, 'image', url)}
                              onUploadingChange={(u) => {
                                setCoachUploading(prev => ({ ...prev, [coach.id]: u }));
                                setUploadingCount(c => Math.max(0, u ? c + 1 : c - 1));
                              }}
                              label="Photo"
                            />
                          </div>
                        )}
                      </div>
                      <div className="coach-form-fields">
                        <input
                          type="text"
                          className="form-input"
                          placeholder={`Coach ${index + 1} name`}
                          value={coach.name}
                          onChange={(e) => updateCoachField(coach.id, 'name', e.target.value)}
                          required
                        />
                        <select
                          className="form-input"
                          value={coach.coachTitle || 'Coach'}
                          onChange={(e) => updateCoachField(coach.id, 'coachTitle', e.target.value)}
                          aria-label={`Coach ${index + 1} role`}
                        >
                          {COACH_TITLES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeCoach(coach.id)}
                        className="coach-remove-btn"
                        title="Remove coach"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description" className="form-label">Description</label>
                <BodyField
                  id="description"
                  value={formData.description}
                  rich={richTextAllowed(plan, features)}
                  onChange={description => setFormData({ ...formData, description })}
                  required
                  rows={5}
                  placeholder="Describe the team, training times, achievements, etc."
                />
              </div>

              <ImageUploader
                onUpload={handleImageUpload}
                onUploadingChange={(u) => setUploadingCount(c => Math.max(0, u ? c + 1 : c - 1))}
                currentImage={formData.image}
                label="Team Image"
              />

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="ebtn ebtn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ebtn ebtn-primary"
                  disabled={isSaving || isUploading}
                >
                  {isUploading ? 'Uploading photo…' : isSaving ? 'Saving...' : editingTeam ? 'Update Team' : 'Create Team'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Teams;
